﻿<template>
    <div class="mx-auto px-4 py-6 flex flex-col items-center gap-8">

        <div v-if="!isAgent_Login() && show_redirect_msg && !show_payment_methods" class="flex flex-col items-center gap-3 text-center">
            <svg class="w-10 h-10 text-pink-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
            </svg>
            <h2 class="text-lg font-semibold text-gray-800">Taking you to our secure payment portal…</h2>
            <p class="text-sm text-gray-500">You'll be redirected automatically. Please don't close this page.</p>
        </div>

        <div v-if="!isAgent_Login() && show_fallback_button && show_redirect_msg && !show_payment_methods" class="w-full flex flex-col items-center gap-2">
            <p class="text-sm text-gray-500">Redirect didn't open automatically?</p>
            <button type="button" class="button button-pink-book full" v-on:click="redirect_to_paymentprovider()">
                Make payment now
            </button>
        </div>


		<div v-if="show_payment_methods" class="w-full flex flex-col items-center gap-2">
			<div class="w-full max-w-lg mx-auto">
				<p class="text-md font-semibold uppercase tracking-wider text-gray-500 mb-3">
					Select Payment Method
				</p>
				<div class="flex flex-col gap-3 w-full">
                    <template v-for="(group, idx) in groupedProviderMethods" :key="idx">

                        <button
                            v-if="group.type === 'full'"
                            type="button"
                            :class="[
                                'w-full min-w-0 flex flex-col p-4 rounded-2xl transition-all duration-200 text-left focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1',
                                selected_payment_provider === group.method.provider_name && selected_category_id === group.method.categoryid
                                    ? 'ring-2 ring-indigo-600 bg-indigo-50/40 shadow-[0_8px_24px_-4px_rgba(79,70,229,0.35)]'
                                    : 'bg-white shadow-[0_4px_16px_-2px_rgba(15,23,42,0.18)] hover:shadow-[0_8px_24px_-4px_rgba(15,23,42,0.28)]'
                            ]"
                            @click="set_payment_provider_and_checkout(group.method.provider_name, group.method.profile_id, group.method.categoryid)">
                            <div class="flex flex-row items-center gap-3 w-full">
                                <span
                                    class="flex items-center justify-center h-5 w-5 rounded-full border-2 shrink-0"
                                    :class="selected_payment_provider === group.method.provider_name && selected_category_id === group.method.categoryid
                                        ? 'border-indigo-600'
                                        : 'border-gray-300'">
                                    <span
                                        v-if="selected_payment_provider === group.method.provider_name && selected_category_id === group.method.categoryid"
                                        class="h-2.5 w-2.5 rounded-full bg-indigo-600"></span>
                                </span>
                                <div class="h-11 w-11 flex items-center justify-center shrink-0 rounded-xl bg-gray-100 overflow-hidden">
                                    <img
                                        v-if="getGroupLogo(group.method)?.img"
                                        :src="getGroupLogo(group.method)?.img"
                                        :alt="group.method.title"
                                        class="max-h-7 max-w-7 object-contain">
                                    <span
                                        v-else-if="getGroupLogo(group.method)?.svg"
                                        v-html="getGroupLogo(group.method)?.svg"
                                        :alt="group.method.title"
                                        class="max-h-7 max-w-7 object-contain">
                                    </span>
                                </div>
                                <div class="flex-1 min-w-0">
                                    <span class="block text-sm font-bold text-gray-900 leading-snug">{{ group.method.title }}</span>
                                    <span class="block text-xs text-gray-500">{{ group.method.subtitle }}</span>
                                </div>
                                <svg class="h-4 w-4 text-gray-400 shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                    <path fill-rule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clip-rule="evenodd"/>
                                </svg>
                            </div>

                            <div
                                v-if="group.method.showaccepts == true && getVisibleAccepts(group.method.accepts).length > 0"
                                class="flex flex-wrap items-center gap-1.5 mt-3 pt-2.5 border-t border-gray-100/80 w-full"
                                style="border-color: lightgrey;">
                                <span class="text-[11px] text-gray-400 font-medium mr-1">{{ group.method.acceptsLabel ?? 'Accepts:' }}</span>
                                <span
                                    v-for="badge in getVisibleAccepts(group.method.accepts)"
                                    :key="badge"
                                    class="inline-flex items-center justify-center h-8 bg-gray-50 border border-gray-200 rounded-md shadow-2xs overflow-hidden"
                                    :class="show_logos && getLogoById(badge) ? 'aspect-[3/2]' : 'min-w-[2.75rem] px-1.5 py-1'"
                                    :title="badge">
                                    <img
                                        v-if="show_logos && getLogoById(badge)?.img"
                                        :src="getLogoById(badge)?.img"
                                        :alt="getLogoById(badge)?.name"
                                        class="h-full w-full object-cover"
                                    >
                                    <span
                                        v-else-if="show_logos && getLogoById(badge)?.svg"
                                        v-html="getLogoById(badge)?.svg"
                                        class="flex items-center justify-center h-full w-full [&>svg]:h-full [&>svg]:w-full [&>svg]:object-contain"
                                    ></span>
                                    <span v-if="!show_logos || getLogoById(badge) == null" class="text-[10px] font-semibold tracking-wide text-gray-600">{{ getLogoById(badge)?.name ?? badge }}</span>
                                </span>
                            </div>

                            <div
                                v-if="group.method.provider_name === 'paypal' && selected_payment_provider === group.method.provider_name && !hide_payment_paypal"
                                class="w-full max-w-full min-w-0 mt-3 pt-3 border-t border-gray-200/60 overflow-hidden"
                                @click.stop>
                                <div id="paypal-button-container" class="mt-2"/>
                                <p id="paypal-result-message" class="text-xs text-red-500 mt-2"/>
                            </div>
                        </button>

                        <div v-else class="grid grid-cols-2 gap-2 w-full">
                            <button
                                v-for="method in group.methods"
                                :key="method.categoryid"
                                type="button"
                                class="flex items-center justify-center rounded-xl h-14 px-3 overflow-hidden transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1"
                                :style="{
                                    backgroundColor: method.buttonBg || '#ffffff',
                                    border: method.buttonBorder ? ('1px solid ' + method.buttonBorder) : 'none'
                                }"
                                :class="[
                                    method.buttonBorder ? 'shadow-sm hover:shadow-md' : 'shadow-[0_4px_16px_-2px_rgba(15,23,42,0.18)] hover:shadow-[0_8px_24px_-4px_rgba(15,23,42,0.28)]',
                                    selected_payment_provider === method.provider_name && selected_category_id === method.categoryid ? 'ring-2 ring-indigo-600 ring-offset-1' : ''
                                ]"
                                @click="set_payment_provider_and_checkout(method.provider_name, method.profile_id, method.categoryid)">
                                <img
                                    v-if="getLogoById(method.categoryid)?.img"
                                    :src="getLogoById(method.categoryid)?.img"
                                    :alt="getLogoById(method.categoryid)?.name"
                                    class="h-full w-full object-contain">
                                <span
                                    v-else
                                    v-html="getLogoById(method.categoryid)?.svg"
                                    class="h-full w-full flex items-center justify-center [&>svg]:h-full [&>svg]:w-auto"></span>
                            </button>
                        </div>

                    </template>
				</div>
				<div class="flex items-center justify-center gap-2 mt-4 text-xs text-gray-500">
					<svg class="h-3.5 w-3.5 shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
						<path fill-rule="evenodd" d="M10 1a4 4 0 00-4 4v2H5a2 2 0 00-2 2v7a2 2 0 002 2h10a2 2 0 002-2V9a2 2 0 00-2-2h-1V5a4 4 0 00-4-4zm2 6V5a2 2 0 10-4 0v2h4z" clip-rule="evenodd"/>
					</svg>
					<span>Your payment information is secure and encrypted.</span>
				</div>
			</div>
		</div>



    <div v-if="show_hints" class="w-full border border-pink-200 rounded-lg bg-pink-50 divide-y divide-pink-200 text-sm text-gray-700">
            <div class="flex gap-3 px-4 py-3">
                <svg class="w-4 h-4 mt-0.5 shrink-0 text-pink-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"/>
                </svg>
                <p class="m-0 p-0">Please ensure your <strong>address matches the details with your bank or credit card company</strong>. A mismatch could result in a decline.</p>
            </div>
            <div class="flex gap-3 px-4 py-3">
                <svg class="w-4 h-4 mt-0.5 shrink-0 text-pink-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"/>
                </svg>
                <p class="m-0 p-0">You may receive an <strong>authentication message from your card issuer</strong>. If your card doesn't participate, you'll be returned here to complete your order.</p>
            </div>
        </div>

        <div v-if="!initialised_payment && !show_payment_methods" class="w-full">
            <div class="flex flex-col items-center justify-center p-6 bg-white rounded-lg space-y-3 select-none">
                <svg class="animate-spin h-12 w-12 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
            </div>
        </div>

        <div :hidden="hide_payment_ttcgateway" class="w-full h-full shadow-lg shadow-black rounded-lg border border-black border-solid p-2" :id="credit_card_div" style="background-color: #ede8da; border-color: lightgrey;"></div>
        <div :hidden="hide_payment_paymentprovider_button" class="w-full" :id="paymentprovider_div_button">
            <button type="button" class="button button-pink-book full" v-on:click="redirect_to_paymentprovider()">
                Make payment now
            </button>
        </div>
        <div :hidden="hide_payment_paymentprovider_iframe" class="w-full h-full shadow-lg shadow-black rounded-lg border border-black border-solid p-2" :id="paymentprovider_div_frame" style="background-color: #F5F5F5; border-color: lightgrey; height:800px;">
            <iframe style="height:100%; width:100%;" v-on:load="onLoadIframe" name="paymentprovider_iframe" :src="paymentiframe_url"></iframe>
        </div>

        <div v-if="!finalised_payment" class="w-full">
            <button type="button" disabled class="button button-pink-book full">
                <svg class="animate-spin h-5 w-5 mr-3 inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 512 512" stroke="currentColor">
                    <path fill="currentColor" d="M440.65 12.57l4 82.77A247.16 247.16 0 0 0 255.83 8C134.73 8 33.91 94.92 12.29 209.82A12 12 0 0 0 24.09 224h49.05a12 12 0 0 0 11.67-9.26 175.91 175.91 0 0 1 317-56.94l-101.46-4.86a12 12 0 0 0-12.57 12v47.41a12 12 0 0 0 12 12H500a12 12 0 0 0 12-12V12a12 12 0 0 0-12-12h-47.37a12 12 0 0 0-11.98 12.57zM255.83 432a175.61 175.61 0 0 1-146-77.8l101.8 4.87a12 12 0 0 0 12.57-12v-47.4a12 12 0 0 0-12-12H12a12 12 0 0 0-12 12V500a12 12 0 0 0 12 12h47.35a12 12 0 0 0 12-12.6l-4.15-82.57A247.17 247.17 0 0 0 255.83 504c121.11 0 221.93-86.92 243.55-201.82a12 12 0 0 0-11.8-14.18h-49.05a12 12 0 0 0-11.67 9.26A175.86 175.86 0 0 1 255.83 432z"/>
                </svg>
                Finalising Payment
            </button>
            <p class="text-sm text-gray-500">You'll be redirected automatically. Please don't close this page.</p>
        </div>
    </div>
</template>

<script lang="ts" src="./payment_credit_card.ts"></script>